import { useNavigate } from 'react-router-dom'
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Button } from '@/components/ui/button'
import type { ProcessingStatus } from '@/hooks/useProcessingStatus'

interface ProcessingStatusCardProps {
  status: ProcessingStatus
}

export function ProcessingStatusCard({ status }: ProcessingStatusCardProps) {
  const navigate = useNavigate()
  const { state, progress, stage, errorMessage, resourceId, reset } = status

  if (state === 'idle') return null

  if (state === 'processing') {
    return (
      <Card>
        <CardContent className="flex flex-col gap-3 p-6">
          <div className="flex items-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <span className="text-sm font-medium">Processing model...</span>
          </div>
          <Progress value={progress} />
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{stage || 'Initializing...'}</span>
            <span>{progress}%</span>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (state === 'complete') {
    return (
      <Card className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
        <CardContent className="flex items-center justify-between p-6">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
            <span className="text-sm font-medium text-green-700 dark:text-green-300">
              Processing complete
            </span>
          </div>
          <div className="flex items-center gap-2">
            {resourceId && (
              <Button
                size="sm"
                className="cursor-pointer"
                onClick={() => navigate(`/store-models/${resourceId}`)}
              >
                View Model
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="cursor-pointer"
              onClick={reset}
            >
              Dismiss
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  // error state
  return (
    <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950">
      <CardContent className="flex items-center justify-between p-6">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
          <span className="text-sm font-medium text-red-700 dark:text-red-300">
            {errorMessage ?? 'Processing failed'}
          </span>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="cursor-pointer"
          onClick={reset}
        >
          Dismiss
        </Button>
      </CardContent>
    </Card>
  )
}
